import { gql, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { Link } from 'react-router-dom';

import Button from '../../components/Button';
import InspectLinkedText from '../../components/InspectLinkedText';
import { assistantThreadDeepLinkPath } from '../../paths';

import CaregiverOnlyBadge from './CaregiverOnlyBadge';
import { abbreviateId, formatWorkItemTimestampFull } from './workItemsView';

import type {
  AdminListWorkItemJournalEntriesQuery,
  AdminListWorkItemJournalEntriesQueryVariables,
} from './__generated__/WorkItemJournalModal.gql';

const ADMIN_LIST_WORK_ITEM_JOURNAL_ENTRIES = gql`
  query AdminListWorkItemJournalEntries($workItemId: ID!, $cursor: String) {
    adminListWorkItemJournalEntries(workItemId: $workItemId, cursor: $cursor) {
      hasMore
      nextCursor
      itemCaregiverReleased
      entries {
        id
        body
        authorEmail
        sourceThreadId
        caregiverOnly
        createdAt
      }
    }
  }
`;

type JournalEntry = AdminListWorkItemJournalEntriesQuery['adminListWorkItemJournalEntries']['entries'][number];

/**
 * What an entry's badge explains. Narrower than the item's on purpose: this is
 * a column on the entry recording the seat that wrote it, so it never clears —
 * a release dims it instead.
 */
const ENTRY_HELD_TITLE =
  "Written from a caregiver's own session, which withholds this entry from a loved one until the item is released.";

const ENTRY_RELEASED_TITLE =
  "Written from a caregiver's own session. The item has since been released, so a loved one reads this entry too.";

interface IJournalEntryProps {
  entry: JournalEntry;
  familyId: string;
  itemReleased: boolean;
}

/** One entry: who wrote it and from which run, the time on the far side, then the entry as written. */
const JournalEntryBlock = ({ entry, familyId, itemReleased }: IJournalEntryProps) => {
  const threadId = entry.sourceThreadId ?? null;
  return (
    <div className="border-border-primary flex flex-col gap-1.5 border-t pt-3 first:border-t-0 first:pt-0">
      <div className="flex items-baseline gap-2">
        <span className="text-fg-primary text-xs font-medium">{entry.authorEmail ?? 'Assistant'}</span>
        {entry.caregiverOnly && (
          <CaregiverOnlyBadge title={itemReleased ? ENTRY_RELEASED_TITLE : ENTRY_HELD_TITLE} released={itemReleased} />
        )}
        {threadId !== null && (
          <Link
            to={assistantThreadDeepLinkPath({ familyId, threadId })}
            target="_blank"
            rel="noopener noreferrer"
            className="text-fg-brand-primary hover:text-fg-brand-secondary font-mono text-[10px] underline-offset-2 hover:underline"
            title={threadId}
          >
            {abbreviateId(threadId)}
          </Link>
        )}
        <span className="text-fg-tertiary ml-auto shrink-0 font-mono text-[10px] tabular-nums" title={entry.createdAt}>
          {formatWorkItemTimestampFull(entry.createdAt)}
        </span>
      </div>
      <div className="text-fg-secondary whitespace-pre-wrap break-words font-mono text-xs">
        <InspectLinkedText text={entry.body} />
      </div>
    </div>
  );
};

interface IWorkItemJournalModalProps {
  /** The item whose record to show, with the family its thread links open under; null keeps the modal closed. */
  workItem: { id: string; familyId: string } | null;
  onClose: () => void;
}

/**
 * The whole record of one work item, newest first, read a page at a time from
 * `adminListWorkItemJournalEntries` — a row's `journal` is only a preview
 * window, so the modal fetches rather than reading the row.
 */
const WorkItemJournalModal = ({ workItem, onClose }: IWorkItemJournalModalProps) => {
  const { data, loading, error, fetchMore, refetch } = useQuery<
    AdminListWorkItemJournalEntriesQuery,
    AdminListWorkItemJournalEntriesQueryVariables
  >(ADMIN_LIST_WORK_ITEM_JOURNAL_ENTRIES, {
    variables: { workItemId: workItem?.id ?? '', cursor: null },
    skip: workItem === null,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const listing = data?.adminListWorkItemJournalEntries;
  const entries = listing?.entries ?? [];
  const hasMore = listing?.hasMore ?? false;
  const nextCursor = listing?.nextCursor ?? null;
  const itemReleased = listing?.itemCaregiverReleased ?? false;

  const handleLoadMore = async () => {
    if (workItem === null || !hasMore || nextCursor === null) {
      return;
    }
    await fetchMore({
      variables: { workItemId: workItem.id, cursor: nextCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        // The cursor is an offset, so an entry written since the last page pushes that page's last row onto this one.
        const shown = new Set(prev.adminListWorkItemJournalEntries.entries.map((entry) => entry.id));
        return {
          adminListWorkItemJournalEntries: {
            ...fetchMoreResult.adminListWorkItemJournalEntries,
            entries: [
              ...prev.adminListWorkItemJournalEntries.entries,
              ...fetchMoreResult.adminListWorkItemJournalEntries.entries.filter((entry) => !shown.has(entry.id)),
            ],
          },
        };
      },
    });
  };

  return (
    <Modal isOpen={workItem !== null} onOpenChange={(open) => !open && onClose()} size="6xl">
      <Modal.Header onClose={onClose}>Journal</Modal.Header>
      <Modal.Body>
        {workItem !== null && (
          <div className="flex max-h-[75vh] w-full flex-col gap-2 overflow-y-auto text-xs">
            {error !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-fg-error-primary">The record did not load.</span>
                <Button color="secondary" size="xs" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            )}
            {loading && entries.length === 0 && <span className="text-fg-secondary">Loading…</span>}
            {!loading && error === undefined && entries.length === 0 && (
              <span className="text-fg-tertiary italic">No entries yet.</span>
            )}
            {entries.map((entry) => (
              <JournalEntryBlock
                key={entry.id}
                entry={entry}
                familyId={workItem.familyId}
                itemReleased={itemReleased}
              />
            ))}
            {entries.length > 0 && (
              <div className="border-border-primary flex items-center gap-3 border-t pt-2">
                <span className="text-fg-tertiary">
                  {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                  {hasMore ? ' so far' : ''}
                </span>
                {hasMore && (
                  <Button color="secondary" size="xs" onClick={() => void handleLoadMore()} isLoading={loading}>
                    Load more
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default WorkItemJournalModal;
